import { useState } from 'react';
import { Mail } from 'lucide-react';
import {
  SystemRole,
  TASK_DIGEST_DAYS_SETTING,
  TASK_DIGEST_HOUR_SETTING,
  describeDigestLookAhead,
  formatDigestHour,
  taskDigestSchedule,
  wantsDailyTaskEmail,
  type User,
} from '@peles/shared';
import { useAppSettingValue } from '../../hooks/useAppSettingValue';
import { useUpdateUser } from '../../hooks/useUsers';

/**
 * What the email holds, from the office's schedule (Settings → App Settings).
 * A client or vendor cannot read settings, so theirs is said without the numbers.
 */
function useDigestDescription(isClient: boolean): string {
  const hour = useAppSettingValue(TASK_DIGEST_HOUR_SETTING, { enabled: !isClient });
  const days = useAppSettingValue(TASK_DIGEST_DAYS_SETTING, { enabled: !isClient });
  if (isClient) {
    return 'Each morning, the tasks assigned to you that are overdue, due today or coming up soon.';
  }
  const schedule = taskDigestSchedule(hour.data, days.data);
  const ahead = describeDigestLookAhead(schedule.daysAhead);
  const which = ahead ? `overdue, due today or due ${ahead}` : 'overdue or due today';
  return `Each morning at ${formatDigestHour(schedule.hour)}, your open tasks that are ${which}. Nothing is sent on a day with nothing due.`;
}

/**
 * A person's own switch for the daily task email.
 *
 * Unset, it follows the role: the office gets it, a client or vendor does not
 * until they ask — so the checkbox shows what will actually happen, and ticking it stores
 * the choice. It has its own switch rather than a row in the notification
 * preferences because there, anything nobody has touched counts as on.
 */
export function DailyTaskEmailSetting({ user }: { user: User }) {
  const updateUser = useUpdateUser();
  const [error, setError] = useState<string | null>(null);
  // The choice on its way to the server, shown until the saved user arrives —
  // otherwise the box flicks back to the old value mid-save.
  const [pending, setPending] = useState<boolean | null>(null);
  const on = pending ?? wantsDailyTaskEmail(user.systemRole, user.dailyTaskEmail);
  const isClient = user.systemRole === SystemRole.CLIENT || user.systemRole === SystemRole.VENDOR;
  const description = useDigestDescription(isClient);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
          checked={on}
          disabled={updateUser.isPending}
          onChange={(e) => {
            const choice = e.target.checked;
            setError(null);
            setPending(choice);
            updateUser.mutate(
              { id: user.id, updates: { dailyTaskEmail: choice } },
              {
                onError: () => setError('Your choice could not be saved. Try again.'),
                onSettled: () => setPending(null),
              },
            );
          }}
        />
        <span>
          <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <Mail className="h-4 w-4 text-gray-500" aria-hidden="true" />
            Daily task email
          </span>
          <span className="block text-sm text-gray-500 mt-0.5">{description}</span>
        </span>
      </label>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
